import React, { memo, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Text, useTheme } from '@Sage/hoi-poi-ui';
import EntityLink from 'components/EntityLink';
import { CONTACTS, COMPANIES } from 'constants/Entities';
import { getLiteral } from 'utils/getLiteral';
import { getLocalDate } from 'utils/dates';
import { getSrcContactCircleAvatar } from 'utils/getSrcAvatar';
import { getCompanyDetailUrl } from 'utils/getUrl';
import { getBackendBoolean } from 'utils/fm';
import { stopEntityNavigation } from 'utils/entityNavigation';
import { canViewEntity } from 'utils/entityPermissions';
import WidgetDetailHeader from 'containers/components/widgets/components/WidgetDetailHeader';
import useEntityDetail from 'containers/components/EntityDetail/hooks/useEntityDetail';
import CompanyChip from 'containers/components/chips/CompanyChip';
import useCrudModal from 'containers/components/EntityCrud/hooks/useCrudModal';

const propTypes = {
    contact: PropTypes.object,
};

const ContactDetailWidgetHeader = memo(({ contact }) => {
    const theme = useTheme();
    const { openTab, hasTabsEnabled } = useEntityDetail();
    const { getCreateActions } = useCrudModal();
    const canEdit = useSelector(
        (state) => !!state.config.permission.crud_permission?.[CONTACTS.permission]?.update,
    );

    const onClickCompany = useCallback(
        (e) => {
            if (!canViewEntity(COMPANIES)) return stopEntityNavigation(e);
            if (!hasTabsEnabled || e.ctrlKey) return true;

            e.stopPropagation();
            e.preventDefault();

            openTab({
                entity: COMPANIES,
                id: contact.idCompany,
                hasCrudInDetail: false,
                toTab: true,
            });
        },
        [contact, hasTabsEnabled, openTab],
    );
    const renderSubtitle = useMemo(() => {
        if (contact.contactType && contact.idCompany && contact.company) {
            return (
                <div className="fm-widget-detail__header__subtitle__content">
                    <Text type="subtitle1" color={theme.colors.utility.textSecondary}>
                        {contact.contactType}
                    </Text>
                    <Text
                        type="subtitle1"
                        color={theme.colors.utility.textSecondary}
                    >{` ${getLiteral('action_at')} `}</Text>
                    <CompanyChip
                        idCompany={contact.idCompany}
                        remotePopover={true}
                        onClickPopover={onClickCompany}
                    >
                        <EntityLink
                            entity={COMPANIES}
                            href={getCompanyDetailUrl(contact.idCompany)}
                            onClick={onClickCompany}
                            linkProps={{ type: 'subtitle1', target: 'blank' }}
                            textProps={{ type: 'subtitle1' }}
                        >
                            {contact.company}
                        </EntityLink>
                    </CompanyChip>
                </div>
            );
        } else if (contact.contacType && !(contact.idCompany && contact.company)) {
            return contact.contactType;
        } else if (!contact.contacType && contact.idCompany && contact.company) {
            return (
                <CompanyChip
                    idCompany={contact.idCompany}
                    remotePopover={true}
                    onClickPopover={onClickCompany}
                >
                    <EntityLink
                        entity={COMPANIES}
                        href={getCompanyDetailUrl(contact.idCompany)}
                        onClick={onClickCompany}
                        linkProps={{ type: 'subtitle1', target: 'blank' }}
                        textProps={{ type: 'subtitle1' }}
                    >
                        {contact.company}
                    </EntityLink>
                </CompanyChip>
            );
        } else {
            return null;
        }
    }, [contact, onClickCompany, theme]);

    const urlLogo = useMemo(() => {
        return getSrcContactCircleAvatar(contact.id);
    }, [contact]);

    const fullName = useMemo(() => {
        let name = contact.name;
        if (contact.surnames) name = `${name} ${contact.surnames}`;
        return name;
    }, [contact]);

    const crudActions = useMemo(() => {
        let actionsOrder = [
            {
                name: 'createEmail',
                iconName: 'email',
                label: getLiteral('placeholder_send_email'),
            },
            {
                name: 'createActivity',
                iconName: 'activities',
                label: getLiteral('label_activity'),
                crudProps: {
                    data: {
                        idContacto: { label: fullName, value: contact.id },
                        idEmpresa: { label: contact.company, value: contact.idCompany },
                    },
                    isDetailCreateGlobal: true,
                },
            },
            {
                name: 'createActivityWhatsApp',
                iconName: 'whatsapp',
                label: getLiteral('title_whatsapp_activity'),
                crudProps: {
                    data: {
                        idContacto: { label: fullName, value: contact.id },
                        idEmpresa: { label: contact.company, value: contact.idCompany },
                    },
                    isDetailCreateGlobal: true,
                },
            },
            {
                name: 'createEvent',
                iconName: 'calendar',
                label: getLiteral('label_event'),
                crudProps: {
                    data: {
                        contact: [{ label: fullName, value: contact.id }],
                        account: { label: contact.company, value: contact.idCompany },
                    },
                    isDetailCreateGlobal: true,
                },
            },
            {
                name: 'createTask',
                iconName: 'checkCircle',
                label: getLiteral('label_task'),
                crudProps: {
                    data: {
                        contact: [{ label: fullName, value: contact.id }],
                        account: { label: contact.company, value: contact.idCompany },
                    },
                    isDetailCreateGlobal: true,
                },
            },
        ];

        let finalActions = getCreateActions(actionsOrder);

        return finalActions;
    }, [getCreateActions, contact, fullName]);

    const metadata = useMemo(() => {
        return {
            id: contact.id,
            extId: contact.extId,
            importDate: contact.importDate,
            level: contact.securityLevel,
            audit: {
                ...contact.audit,
                readOnlyDb: contact.audit?.readOnlyDb,
            },
        };
    }, [contact]);

    const isReadOnly = canEdit && getBackendBoolean(contact.isReadOnly);

    return (
        <WidgetDetailHeader
            entity={CONTACTS}
            leftItem={
                <div className="contact-info-widget-header__image">
                    <img
                        src={urlLogo.src || urlLogo.fallbackSrc}
                        onError={(e) => {
                            e.target.src = urlLogo.fallbackSrc;
                        }}
                    />
                </div>
            }
            title={fullName}
            subtitle={renderSubtitle}
            metadata={metadata}
            crudActions={crudActions}
            isReadOnly={isReadOnly}
        />
    );
});

ContactDetailWidgetHeader.propTypes = propTypes;

export default ContactDetailWidgetHeader;
